import { useEffect, useState, useRef } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import {
  ArrowLeft, Printer, Download, Loader2, CheckSquare, BookOpen,
  Gamepad2, ShieldCheck, ArrowRight,
} from "lucide-react";
import SEO from "@/components/SEO";

export default function AcademyChecklist() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const autoPrint = searchParams.get("print") === "1";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [checked, setChecked] = useState({});
  const printRef = useRef(null);

  useEffect(() => {
    setData(null); setErr(null);
    api.get(`/academy/checklists/${slug}`)
      .then(({ data: d }) => setData(d))
      .catch((e) => setErr(formatApiError(e)));
  }, [slug]);

  useEffect(() => {
    if (data && autoPrint) {
      // Small delay so the page has rendered before the print dialog opens.
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [data, autoPrint]);

  function toggle(key) {
    setChecked((p) => ({ ...p, [key]: !p[key] }));
  }
  function handlePrint() { window.print(); }
  function handleDownload() {
    // "Save as PDF" via the same browser dialog: most reliable cross-platform v1.
    window.print();
  }

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-4 text-sm">{err}</div>
        <Link to="/academy" className="inline-block mt-4 text-sm text-emerald-700 hover:underline">← Back to Renter Guides & Games</Link>
      </div>
    );
  }
  if (!data) {
    return <div className="max-w-3xl mx-auto px-4 py-16 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading checklist…</div>;
  }

  const backTo = data.audience === "business" ? "/businesses/academy" : "/academy";

  return (
    <div data-testid="academy-checklist-page" className="bg-[#FAFAFA] min-h-[80vh]">
      <SEO
        title={`${data.title}: ConfirmPH Checklist`}
        description={(data.summary || data.intro || data.title || "ConfirmPH checklist.").slice(0, 200)}
        canonical={`/academy/checklists/${slug}`}
        og={{ type: "article" }}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 print:py-0 print:px-0 print:max-w-full">

        {/* Top bar: hidden when printing */}
        <div className="flex items-center justify-between gap-3 flex-wrap print:hidden">
          <Link to={backTo} data-testid="checklist-back-link"
                 className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-emerald-700">
            <ArrowLeft className="w-4 h-4" /> Back to Guides & Games
          </Link>
          <div className="flex gap-2">
            <button data-testid="checklist-print-button"
                    onClick={handlePrint}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm hover:bg-slate-50">
              <Printer className="w-4 h-4" /> Print
            </button>
            <button data-testid="checklist-download-button"
                    onClick={handleDownload}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-medium hover:bg-[#143027]">
              <Download className="w-4 h-4" /> Download (PDF)
            </button>
          </div>
        </div>

        {/* Checklist body */}
        <article ref={printRef} className="mt-6 print:mt-0">
          <header className="border-b border-slate-200 pb-5 mb-6">
            <div className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold inline-flex items-center gap-1.5 print:text-slate-700">
              <CheckSquare className="w-3.5 h-3.5" /> Renter checklist
            </div>
            <h1 data-testid="checklist-title"
                 className="font-display text-3xl sm:text-4xl font-semibold mt-2 leading-tight print:text-2xl">
              {data.title}
            </h1>
            {data.subtitle && (
              <p className="text-slate-600 text-base sm:text-lg mt-2 print:text-sm">{data.subtitle}</p>
            )}
            {data.description && (
              <p className="text-slate-700 text-sm mt-4 print:text-xs">{data.description}</p>
            )}
            <div className="mt-4 text-[10px] uppercase tracking-[0.18em] text-slate-500 hidden print:block">
              ConfirmPH · Renter Safety Checklist · confirm.ph/academy
            </div>
          </header>

          <div className="space-y-7" data-testid="checklist-sections">
            {data.sections.map((section, sIdx) => (
              <section key={sIdx} className="break-inside-avoid">
                <h2 className="font-display text-xl font-semibold text-slate-900 mb-3 print:text-base print:mb-2">
                  {section.title}
                </h2>
                <ul className="space-y-2">
                  {section.items.map((item, iIdx) => {
                    const key = `${sIdx}-${iIdx}`;
                    const isChecked = !!checked[key];
                    return (
                      <li key={key}
                          data-testid={`checklist-item-${sIdx}-${iIdx}`}
                          className="flex items-start gap-3 text-sm leading-relaxed">
                        <button onClick={() => toggle(key)}
                                aria-label={`Toggle item ${iIdx + 1}`}
                                className={`mt-0.5 w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition print:bg-white print:border-slate-700 ${
                                  isChecked
                                    ? "bg-emerald-600 border-emerald-600 print:bg-white"
                                    : "border-slate-400 bg-white hover:border-emerald-500"
                                }`}>
                          {isChecked && <CheckSquare className="w-3.5 h-3.5 text-white print:text-black" strokeWidth={3} />}
                        </button>
                        <span className={`text-slate-800 print:text-black ${isChecked ? "line-through opacity-60 print:line-through" : ""}`}>
                          {item}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>

          <footer className="mt-10 pt-5 border-t border-slate-200 text-[11px] text-slate-500 hidden print:block">
            ConfirmPH: confirm.ph · Use this checklist to protect your rental.
            Not legal advice. Updated by the ConfirmPH editorial team.
          </footer>
        </article>

        {/* Related: hidden when printing */}
        <div className="mt-12 grid sm:grid-cols-2 gap-3 print:hidden">
          {data.related_guide_slug && (
            <Link to={`/academy/guide/${data.related_guide_slug}`}
                   data-testid="checklist-related-guide"
                   className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-emerald-300 hover:shadow-md transition">
              <div className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold inline-flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" /> Read the full guide
              </div>
              <div className="font-display text-base font-semibold mt-1.5">How to Rent Safely in the Philippines</div>
              <div className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1">Open guide <ArrowRight className="w-3.5 h-3.5" /></div>
            </Link>
          )}
          {data.related_quiz_slug && (
            <Link to={`/academy/quiz/${data.related_quiz_slug}`}
                   data-testid="checklist-related-quiz"
                   className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-emerald-300 hover:shadow-md transition">
              <div className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold inline-flex items-center gap-1.5">
                <Gamepad2 className="w-3.5 h-3.5" /> Test your decisions
              </div>
              <div className="font-display text-base font-semibold mt-1.5">Rental Safety Quiz for Renters</div>
              <div className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1">Start quiz <ArrowRight className="w-3.5 h-3.5" /></div>
            </Link>
          )}
        </div>

        <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5 print:hidden"
              data-testid="checklist-trust-strip">
          <div className="flex gap-3 items-start">
            <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5" />
            <div className="text-sm text-slate-700">
              ConfirmPH is a free booking-confirmation layer for Philippine rentals.
              Use this checklist for every booking: most disputes lose themselves in the paper trail.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
